import { User } from "./types";

interface UsePermissionProp {
  user: User | null;
}

const RoutesPermissions = {
  NoUser: {
    AdministrationPanel: {
      Acces: true,
      Dashboard: {
        Acces: false,
        Permissions: {
          transactions: false,
          numberofpersons: false,
          deposits: false,
          taxes: false,
          satQamount: false,
          taxesDramount: false,
          serviceCharge: false,
          Income: false,
          Inner: false,
        },
      },
      Analytics: {
        Acces: false,
        Transactions: {
          Acces: true,
          Permissions: {
            enableeditPaymentGategay: true,
            editstatuspermission: true,
            seevoucherinner: false,
            enableeditAdvisor: false,
            reviewTransaction: false,
          },
        },
        Incidents: { Acces: true },
        Pretransactions: { Acces: true },
        PhysicalRegistration: {
          Acces: true,
        },
      },
      Reports: { Acces: false },
      DayClousures: { Acces: false },
      Users: {
        Acces: false,
        Permissions: {
          addUser: true,
          editUser: true,
        },
      },
      Configurations: { Acces: false },
      AdvisorPanel: {
        Acces: false,
        Permissions: {
          enableeditPaymentGategay: true,
          editstatuspermission: true,
          seevoucherinner: false,
        },
      },
    },
    Login: {
      Acces: true,
    },
    Payment: {
      Acces: true,
      PaymentForm: {
        Acces: true,
        Permissions: {
          physicalpayment: false,
          complepayment: false,
          multipayments: false,
          singlepayment: false,
          candedituser: false,
          caneditterminal: false,
        },
      },
      CompletePayment: {
        Acces: true,
      },
      PaymentCompleted: {
        Acces: true,
      },
    },
    PrincipalPage: {
      Acces: true,
      Permissions: {
        dashboardicon: false,
        navigationbar: false,
      },
    },
  },
  DefaultUser: {
    AdministrationPanel: {
      Acces: true,
      Dashboard: {
        Acces: false,
        Permissions: {
          transactions: false,
          numberofpersons: false,
          deposits: false,
          taxes: false,
          satQamount: false,
          taxesDramount: false,
          serviceCharge: false,
          Income: false,
          Inner: false,
        },
      },
      Analytics: {
        Acces: false,
        Transactions: {
          Acces: true,
          Permissions: {
            enableeditPaymentGategay: true,
            editstatuspermission: true,
            seevoucherinner: false,
            enableeditAdvisor: false,
            reviewTransaction: false,
          },
        },
        Incidents: { Acces: true },
        Pretransactions: { Acces: true },
        PhysicalRegistration: { Acces: true },
      },
      Reports: { Acces: false },
      DayClousures: { Acces: false },
      Users: {
        Acces: false,
        Permissions: {
          addUser: true,
          editUser: true,
        },
      },
      Configurations: { Acces: false },
      AdvisorPanel: {
        Acces: true,
        Permissions: {
          enableeditPaymentGategay: false,
          editstatuspermission: false,
          seevoucherinner: false,
        },
      },
    },
    Login: {
      Acces: true,
    },
    Payment: {
      Acces: true,
      PaymentForm: {
        Acces: true,
        Permissions: {
          physicalpayment: true,
          complepayment: false,
          multipayments: false,
          singlepayment: false,
          candedituser: false,
          caneditterminal: false,
        },
      },
      CompletePayment: {
        Acces: true,
      },
      PaymentCompleted: {
        Acces: true,
      },
    },
    PrincipalPage: {
      Acces: true,
      Permissions: {
        dashboardicon: true,
        navigationbar: true,
      },
    },
  },
};

const hasAccess = (path: string, permissions: any) => {
  const cleanPath = path.replace(/\/\*$/, "");
  const segments = cleanPath.split("/").filter(Boolean);
  let currentLevel = permissions;

  for (const segment of segments) {
    if (currentLevel[segment]) {
      currentLevel = currentLevel[segment];

      if (!currentLevel.Acces) {
        return false;
      }
    } else {
      return false;
    }
  }

  return currentLevel.Acces !== false;
};

const getPermissions = (path: string, permissions: any) => {
  const cleanPath = path.replace(/\/\*$/, "");
  const segments = cleanPath.split("/").filter(Boolean);
  let currentLevel = permissions;
  for (const segment of segments) {
    if (currentLevel[segment]) {
      currentLevel = currentLevel[segment];
    } else {
      return {};
    }
  }

  return currentLevel.Permissions || {};
};

/**
 * Hook that calculates permission information for routes based on the
 * permissions stored on the current user.
 *
 * @param user - The current authenticated user.
 * @returns Helpers for validating and retrieving permissions for a route.
 */
const usePermissions = ({ user }: UsePermissionProp) => {
  const validPermissionsbyroute = (route: string): boolean => {

    if (user?.treeFirebase) {
      const permission = typeof user?.treeFirebase === 'string'
        ? JSON.parse(user.treeFirebase)
        : user?.treeFirebase;
      if (permission) {
        return hasAccess(route, permission);
      }
      return hasAccess(route, RoutesPermissions.NoUser);
    }
    return hasAccess(route, RoutesPermissions.NoUser);
  };

  const getRoutePermissions = (route: string): any => {
    if (user?.treeFirebase) {
      const permission = typeof user?.treeFirebase === 'string'
        ? JSON.parse(user.treeFirebase)
        : user?.treeFirebase;

      if (permission) {
        return getPermissions(route, permission);
      }
      return getPermissions(route, RoutesPermissions.NoUser);
    }
    return getPermissions(route, RoutesPermissions.NoUser);
  };

  return {
    getRoutePermissions,
    validPermissionsbyroute,
  };
};

export default usePermissions;
